import React, { useState, useEffect } from 'react';
import styles from './Categories.module.css';
import { DataProviderInstance } from '../../../../api/DataProvider';
import AreaTitle from '../../../../components/areaTitle/AreaTitle';
import CustomButton from '../../../../components/buttons/CustomButton';
import Toast from '../../../../components/support/Toast';
import ConfirmPopup from '../../../../popup/ConfirmPopup/ConfirmPopup';
import { Switch } from "@mui/material";
import IconCategory from "@mui/icons-material/CategoryOutlined";
import IconAdd from "@mui/icons-material/Add";
import { CreateEditCategoryPopup } from './popups/CreateEditCategoryPopup';
import CategoryColumn from './components/CategoryColumn';

/**
 * Confirmação ao excluir: categoria sem lançamentos é excluída; com lançamentos
 * só pode ser inativada (o histórico do financeiro continua lendo o nome dela).
 */
const deleteConfirmProps = (category) => {
  if (!category) return { Title: '', Description: '', ConfirmLabel: '' };
  const usage = Number(category.usageCount) || 0;
  if (usage === 0) {
    return {
      Title: 'Excluir Categoria',
      Description: `Excluir a categoria "${category.name}"? Esta ação não pode ser desfeita.`,
      ConfirmLabel: 'Excluir'
    };
  }
  const entries = usage === 1 ? '1 lançamento' : `${usage} lançamentos`;
  const isActive = !!Number(category.isActive);
  return {
    Title: 'Categoria em uso',
    Description: `"${category.name}" já tem ${entries} e não pode ser excluída. ` +
      (isActive
        ? 'Inative para tirá-la das listas sem perder o histórico.'
        : 'Ela já está inativa e não aparece nas listas.'),
    ConfirmLabel: isActive ? 'Inativar' : 'Entendi'
  };
};

function Categories() {
  const [isLoading, setIsLoading] = useState(true);
  const [categories, setCategories] = useState([]);
  const [showCreateEditPopup, setShowCreateEditPopup] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [showInactive, setShowInactive] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [toast, setToast] = useState({ open: false, message: '', severity: 'success' });

  const {
    GetFinancialCategories,
    SetFinancialCategory,
    UpdateFinancialCategory,
    DeleteFinancialCategory
  } = DataProviderInstance();

  useEffect(() => {
    loadCategories();
  }, []);

  const showToast = (message, severity = 'success') => {
    setToast({ open: true, message, severity });
  };

  const loadCategories = () => {
    setIsLoading(true);
    GetFinancialCategories((response) => {
      setIsLoading(false);
      if (response.status == "success") {
        setCategories(response.data || []);
      } else {
        // TODO: Adicionar tratamento de erro visual quando tivermos o componente
        
      }
    });
  };

  const handleCreateCategory = () => {
    setSelectedCategory(null);
    setShowCreateEditPopup(true);
  };

  const handleEditCategory = (category) => {
    setSelectedCategory(category);
    setShowCreateEditPopup(true);
  };

  const handleSaveCategory = (categoryData) => {
    if (selectedCategory) {
      // Editar categoria existente
      UpdateFinancialCategory(categoryData, (response) => {
        if (response.status == "success") {
          loadCategories();
          setShowCreateEditPopup(false);
          showToast('Categoria atualizada');
        } else {
          showToast(response.message || 'Não foi possível salvar a categoria. Tente novamente.', 'error');
        }
      });
    } else {
      // Criar nova categoria
      SetFinancialCategory(categoryData, (response) => {
        if (response.status == "success") {
          loadCategories();
          setShowCreateEditPopup(false);
          showToast('Categoria criada');
        } else {
          showToast(response.message || 'Não foi possível criar a categoria. Tente novamente.', 'error');
        }
      });
    }
  };

  const confirmDelete = () => {
    const category = deleteTarget;
    if (!category) return;

    // Em uso: o caminho é inativar (ou nada, se já está inativa)
    if (Number(category.usageCount) > 0) {
      if (!Number(category.isActive)) return;
      UpdateFinancialCategory({ ...category, isActive: false }, (response) => {
        if (response.status == "success") {
          loadCategories();
          showToast('Categoria inativada');
        } else {
          showToast(response.message || 'Não foi possível inativar a categoria. Tente novamente.', 'error');
        }
      });
      return;
    }

    DeleteFinancialCategory(category.id, (response) => {
      if (response.status == "success") {
        loadCategories();
        showToast('Categoria excluída');
      } else {
        // O servidor confere o uso de novo (pode ter ganhado lançamento depois de abrir a tela)
        loadCategories();
        showToast(response.message || 'Não foi possível excluir a categoria. Tente novamente.', 'error');
      }
    });
  };

  const columnProps = (type) => {
    const ofType = categories.filter(cat => cat.type == type);
    const visible = ofType.filter(cat => showInactive || cat.isActive);
    return {
      type,
      categories: visible,
      hiddenCount: ofType.length - visible.length,
      isLoading,
      onEdit: handleEditCategory,
      onDelete: setDeleteTarget
    };
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <AreaTitle
          Title="Categorias"
          Description="Organize as entradas e saídas para ler o financeiro de relance."
          Icon={<IconCategory />}
        />
        <div className={styles.actions}>
          <label className={styles.showInactive}>
            <Switch
              size="small"
              checked={showInactive}
              onChange={(e) => setShowInactive(e.target.checked)}
            />
            <span>Exibir inativos</span>
          </label>
          <CustomButton
            variant="contained"
            style="main"
            label="Nova Categoria"
            onClick={handleCreateCategory}
            startIcon={<IconAdd />}
          />
        </div>
      </div>

      <div className={styles.categoriesContainer}>
        <CategoryColumn {...columnProps('income')} />
        <CategoryColumn {...columnProps('expense')} />
      </div>

      {showCreateEditPopup && (
        <CreateEditCategoryPopup
          category={selectedCategory}
          onClose={() => setShowCreateEditPopup(false)}
          onSave={handleSaveCategory}
        />
      )}

      <ConfirmPopup
        Visibility={!!deleteTarget}
        {...deleteConfirmProps(deleteTarget)}
        OnConfirm={confirmDelete}
        OnHide={() => setDeleteTarget(null)}
      />

      <Toast
        open={toast.open}
        onClose={() => setToast({ ...toast, open: false })}
        message={toast.message}
        severity={toast.severity}
      />
    </div>
  );
}

export default Categories;
